import React, { useState, useMemo } from 'react';
import { formatRupiah, formatRupiahFull } from '../data/financialData';
import type { Area } from '../data/financialData';
import { Doughnut, Line } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';
import { parseGLRows, GLRow } from '../utils/budgetMatch';
import { useSmartInsights } from '../SmartInsightsContext';
import SmartTooltip from './SmartTooltip';
import { analyzeKPI, analyzeChartTrend } from '../utils/smartInsightsEngine';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface BalanceSheetPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
  activeEntity?: string;
}

export const BalanceSheetPage: React.FC<BalanceSheetPageProps> = ({
  area,
  branch,
  startIdx,
  endIdx,
  activeEntity
}) => {
  const [mode, setMode] = useState<'consolidated' | 'branch'>('consolidated');
  const [selectedBranch, setSelectedBranch] = useState<string>(branch !== 'ALL' ? branch : '');
  const [monthIdx, setMonthIdx] = useState(endIdx);
  const { chart: chartTheme } = useTheme();
  const { MONTHS, getBranchFullBSAtMonth, branches, balanceSheet, getKPIs } = useData();
  const { isEnabled: insightsEnabled } = useSmartInsights();

  // Active Entity Color Palette
  const accentColor = activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#10b981';
  const accentLight = activeEntity === 'JUARA_GADAI' ? '#FBA94D' : '#34d399';
  const accentGlow = activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : 'rgba(16, 185, 129, 0.15)';

  // Drilldown states
  const [glData, setGlData] = useState<{ columns: string[]; data: any[][] } | null>(null);
  const [breakdownData, setBreakdownData] = useState<Record<string, GLRow[]>>({});
  const [glLoading, setGlLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState<string>('');
  const [drilldownRows, setDrilldownRows] = useState<GLRow[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Expandable sub-accounts states
  const [expandedSubAccounts, setExpandedSubAccounts] = useState<Record<string, boolean>>({});
  const [loadingBreakdown, setLoadingBreakdown] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  // Consolidated view data
  const consolidatedBS = useMemo(() => {
    const areaKey = area as string;
    const bs = (balanceSheet as any)[areaKey] ?? (balanceSheet as any)['ALL'];
    if (!bs) return null;
    const idx = monthIdx;
    return {
      currentAssets: {
        cashBank: bs.currentAssets.cashBank[idx],
        receivables: bs.currentAssets.receivables[idx],
        prepaidAdvances: bs.currentAssets.prepaidAdvances[idx],
        total: bs.currentAssets.total[idx],
      },
      fixedAssets: {
        gross: bs.fixedAssets.gross[idx],
        accumDepreciation: bs.fixedAssets.accumDepreciation[idx],
        net: bs.fixedAssets.net[idx],
      },
      otherAssets: {
        deferredTax: bs.otherAssets.deferredTax[idx],
      },
      totalAssets: bs.totalAssets[idx],
      currentLiabilities: {
        payables: bs.currentLiabilities.payables[idx],
        accruedTax: bs.currentLiabilities.accruedTax?.[idx] ?? 0,
        accruedBPJS: bs.currentLiabilities.accruedBPJS?.[idx] ?? 0,
        accruedSalary: bs.currentLiabilities.accruedSalary[idx],
        accruedExpenses: bs.currentLiabilities.accruedExpenses[idx],
        total: bs.currentLiabilities.total[idx],
      },
      longTermLiabilities: bs.longTermLiabilities[idx],
      totalLiabilities: bs.totalLiabilities[idx],
      equity: {
        openingBalance: bs.equity.openingBalance[idx],
        retainedEarnings: bs.equity.retainedEarnings[idx],
        total: bs.equity.total[idx],
      },
    };
  }, [area, monthIdx, balanceSheet]);

  // Branch view data
  const branchBS = useMemo(() => {
    if (!selectedBranch) return null;
    return getBranchFullBSAtMonth(selectedBranch, monthIdx);
  }, [selectedBranch, monthIdx, getBranchFullBSAtMonth]);

  const activeBS = mode === 'consolidated' ? consolidatedBS : branchBS;

  // Financial Ratios
  const ratios = useMemo(() => {
    if (!activeBS) return null;
    const currentAssets = Math.abs(activeBS.currentAssets.total);
    const currentLiab = Math.abs(activeBS.currentLiabilities.total);
    const totalLiab = Math.abs(activeBS.totalLiabilities);
    const equity = Math.abs(activeBS.equity.total);
    const workingCapital = currentAssets - currentLiab;
    const currentRatio = currentLiab > 0 ? currentAssets / currentLiab : 0;
    const debtToEquity = equity > 0 ? totalLiab / equity : 0;

    return { currentRatio, debtToEquity, workingCapital };
  }, [activeBS]);

  // Donut chart: Assets composition
  const assetsDonut = useMemo(() => {
    if (!activeBS) return null;
    return {
      labels: ['Cash & Bank', 'Receivables', 'Prepaid & Advances', 'Fixed Assets', 'Other Assets'],
      datasets: [{
        data: [
          Math.abs(activeBS.currentAssets.cashBank),
          Math.abs(activeBS.currentAssets.receivables),
          Math.abs(activeBS.currentAssets.prepaidAdvances ?? 0),
          Math.abs(activeBS.fixedAssets.net),
          Math.abs(activeBS.otherAssets.deferredTax),
        ],
        backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', activeEntity === 'JUARA_GADAI' ? '#303795' : '#8b5cf6', '#06b6d4'],
        borderColor: 'transparent',
        borderWidth: 2,
      }],
    };
  }, [activeBS]);

  // Donut chart: Liabilities & Equity
  const liabDonut = useMemo(() => {
    if (!activeBS) return null;
    return {
      labels: ['Current Liabilities', 'Long Term Liabilities', 'Equity'],
      datasets: [{
        data: [
          Math.abs(activeBS.currentLiabilities.total),
          Math.abs(activeBS.longTermLiabilities),
          Math.abs(activeBS.equity.total),
        ],
        backgroundColor: ['#ef4444', '#f59e0b', '#10b981'],
        borderColor: 'transparent',
        borderWidth: 2,
      }],
    };
  }, [activeBS]);

  // Trend line data
  const trendData = useMemo(() => {
    const areaKey = area as string;
    const bs = (balanceSheet as any)[areaKey] ?? (balanceSheet as any)['ALL'];
    if (!bs) return null;

    const labels = MONTHS.slice(startIdx, endIdx + 1).map(m => m.label);
    return {
      labels,
      datasets: [
        {
          label: 'Total Assets',
          data: bs.totalAssets.slice(startIdx, endIdx + 1),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59,130,246,0.1)',
          fill: true, tension: 0.3, pointRadius: 2,
        },
        {
          label: 'Total Liabilities',
          data: bs.totalLiabilities.slice(startIdx, endIdx + 1).map((v: number) => Math.abs(v)),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239,68,68,0.1)',
          fill: true, tension: 0.3, pointRadius: 2,
        },
        {
          label: 'Equity',
          data: bs.equity.total.slice(startIdx, endIdx + 1).map((v: number) => Math.abs(v)),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16,185,129,0.1)',
          fill: true, tension: 0.3, pointRadius: 2,
        },
      ],
    };
  }, [area, startIdx, endIdx, MONTHS, balanceSheet]);

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom' as const, labels: { color: chartTheme.legendColor, font: { size: 10 }, padding: 12 } },
      tooltip: {
        backgroundColor: chartTheme.tooltipBg, titleColor: chartTheme.tooltipTitle,
        bodyColor: chartTheme.tooltipBody, borderColor: chartTheme.tooltipBorder, borderWidth: 1,
        callbacks: { label: (ctx: any) => `${ctx.label}: ${formatRupiah(ctx.raw)}` },
      },
      datalabels: { display: false },
    },
    cutout: '65%',
  };

  // Drilldown data loader & classifier handler
  const handleDrilldown = async (title: string, codes: string[], matches?: (code: string) => boolean) => {
    setDrilldownTitle(title);
    setDrilldownRows([]);
    setGlLoading(true);
    setShowModal(true);

    try {
      const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
      const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';
      
      const targetMonths = MONTHS.slice(0, monthIdx + 1).map(m => m.month);
      const qParams = new URLSearchParams({
        entity: activeEntity ?? 'JUARA_GADAI',
        months: targetMonths.join(','),
        acc_code_prefixes: codes.join(',')
      });
      if (mode === 'branch' && selectedBranch) {
        qParams.set('branch', selectedBranch);
      } else if (mode !== 'branch' && area !== 'ALL') {
        qParams.set('area', area);
      }

      const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${API_KEY}` }
      });
      if (!res.ok) throw new Error('Gagal memuat detail jurnal Buku Besar dari cloud.');
      const loadedData = await res.json();
      const parsedAll = parseGLRows(loadedData.data);

      const filtered = parsedAll.filter((row) => {
        if (matches) {
          return matches(row.accCode);
        }
        return true;
      });

      setDrilldownRows(filtered);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Terjadi kesalahan saat memuat drilldown.');
      setShowModal(false);
    } finally {
      setGlLoading(false);
    }
  };

  const toggleExpand = async (key: string, prefixes: string[], sectionType: 'ASSET' | 'LIABILITY' | 'EQUITY') => {
    if (expandedSubAccounts[key]) {
      setExpandedSubAccounts(prev => ({ ...prev, [key]: false }));
      return;
    }

    if (!breakdownData[key]) {
      setLoadingBreakdown(prev => ({ ...prev, [key]: true }));
      try {
        const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
        const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';
        
        const targetMonths = MONTHS.slice(0, monthIdx + 1).map(m => m.month);
        const qParams = new URLSearchParams({
          entity: activeEntity ?? 'JUARA_GADAI',
          months: targetMonths.join(','),
          acc_code_prefixes: prefixes.join(',')
        });
        if (mode === 'branch' && selectedBranch) {
          qParams.set('branch', selectedBranch);
        } else if (mode !== 'branch' && area !== 'ALL') {
          qParams.set('area', area);
        }

        const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` }
        });
        if (!res.ok) throw new Error('Gagal memuat detail sub account dari cloud.');
        const loadedData = await res.json();
        const parsed = parseGLRows(loadedData.data);
        setBreakdownData(prev => ({ ...prev, [key]: parsed }));
      } catch (err: any) {
        console.error(err);
        alert(err.message || 'Terjadi kesalahan saat memuat detail.');
        return;
      } finally {
        setLoadingBreakdown(prev => ({ ...prev, [key]: false }));
      }
    }

    setExpandedSubAccounts(prev => ({ ...prev, [key]: true }));
  };

  const handleCopyTable = async () => {
    const tableEl = document.querySelector('.paper-table');
    if (!tableEl) return;

    try {
      // 1. Generate text/html for clean copying into Excel/Word
      const clonedTable = tableEl.cloneNode(true) as HTMLTableElement;

      // Remove the expand/collapse buttons so they don't clutter the copy
      clonedTable.querySelectorAll('.expand-btn-toggle').forEach(btn => {
        btn.remove();
      });

      // Style cell text fonts and alignments for rich text copying
      clonedTable.querySelectorAll('td, th').forEach((cell: any) => {
        cell.style.fontFamily = 'Arial, sans-serif';
        if (cell.style.textAlign === 'right') {
          cell.setAttribute('align', 'right');
        }
      });

      const htmlContent = `
        <html>
        <head>
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 11pt; width: 100%; }
            th { border-bottom: 2px solid #000; font-weight: bold; padding: 8px; text-align: left; }
            td { border-bottom: 1px solid #ddd; padding: 8px; text-align: left; }
            .paper-header-row td { font-weight: bold; background-color: #f8fafc; border-bottom: 2px solid #000; }
            .paper-total-row td { font-weight: bold; border-top: 1px solid #000; border-bottom: 4px double #000; }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 20px;">
            <h3 style="margin: 0; font-size: 16pt;">LAPORAN POSISI KEUANGAN</h3>
            <span style="font-size: 11pt; color: #64748b; font-weight: bold;">
              ${activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}
            </span>
            <div style="font-size: 10pt; color: #64748b;">
              Konsolidasi per ${MONTHS[monthIdx]?.label}
            </div>
          </div>
          ${clonedTable.outerHTML}
        </body>
        </html>
      `;

      // 2. Generate plain text (tab-separated values for spreadsheets)
      let plainText = `LAPORAN POSISI KEUANGAN - ${activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}\n`;
      plainText += `Konsolidasi per ${MONTHS[monthIdx]?.label}\n\n`;

      const rows = tableEl.querySelectorAll('tr');
      rows.forEach(row => {
        const cells = row.querySelectorAll('th, td');
        const rowData: string[] = [];
        cells.forEach((cell: any) => {
          let text = cell.innerText || cell.textContent || '';
          // Remove toggle characters and normalize spaces
          text = text.replace(/[▼▶]/g, '').trim();
          rowData.push(text);
        });
        if (rowData.length > 0) {
          plainText += rowData.join('\t') + '\n';
        }
      });

      const blobHtml = new Blob([htmlContent], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': blobHtml,
          'text/plain': blobText
        })
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy balance sheet:', err);
      // Fallback copy using plain text only
      try {
        let plainText = `LAPORAN POSISI KEUANGAN - ${activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}\n`;
        plainText += `Konsolidasi per ${MONTHS[monthIdx]?.label}\n\n`;
        
        const rows = tableEl.querySelectorAll('tr');
        rows.forEach(row => {
          const cells = row.querySelectorAll('th, td');
          const rowData: string[] = [];
          cells.forEach((cell: any) => {
            let text = cell.innerText || cell.textContent || '';
            text = text.replace(/[▼▶]/g, '').trim();
            rowData.push(text);
          });
          if (rowData.length > 0) {
            plainText += rowData.join('\t') + '\n';
          }
        });

        await navigator.clipboard.writeText(plainText);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        alert('Gagal menyalin tabel ke clipboard.');
      }
    }
  };

  const renderSubAccountRow = (
    key: string,
    label: string,
    value: number,
    prefixes: string[],
    sectionType: 'ASSET' | 'LIABILITY' | 'EQUITY',
    customMatches?: (code: string) => boolean,
    isNegative = false
  ) => {
    const isExpanded = expandedSubAccounts[key];
    const isLoading = loadingBreakdown[key];

    const getBreakdown = () => {
      const filtered = breakdownData[key] || [];

      const agg: Record<string, { code: string; name: string; balance: number }> = {};
      filtered.forEach(row => {
        if (!agg[row.accCode]) {
          agg[row.accCode] = {
            code: row.accCode,
            name: row.accName,
            balance: 0
          };
        }
        const val = sectionType === 'ASSET' ? row.amount : -row.amount;
        agg[row.accCode].balance += val;
      });

      return Object.values(agg)
        .filter(x => Math.abs(x.balance) > 0.01)
        .sort((a, b) => a.code.localeCompare(b.code));
    };

    const breakdownItems = isExpanded ? getBreakdown() : [];

    return (
      <>
        <tr>
          <td style={{ paddingLeft: '40px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => toggleExpand(key, prefixes, sectionType)}
              style={{
                background: 'none',
                border: 'none',
                color: accentColor,
                cursor: 'pointer',
                padding: '0 4px',
                fontSize: '11px',
                fontWeight: 'bold',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '18px',
                height: '18px',
                borderRadius: '4px',
                transition: 'background 0.2s',
              }}
              className="expand-btn-toggle"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isLoading ? (
                <span className="mini-spinner" />
              ) : isExpanded ? (
                '▼'
              ) : (
                '▶'
              )}
            </button>
            <span style={{ cursor: 'pointer' }} onClick={() => toggleExpand(key, prefixes, sectionType)}>
              {label}
            </span>
          </td>
          <td style={{ textAlign: 'right', color: isNegative ? '#ef4444' : 'inherit' }}>
            <span 
              className="drillable-value"
              onClick={() => handleDrilldown(label, prefixes, customMatches)}
            >
              {formatRupiah(value)}
            </span>
          </td>
        </tr>

        {isExpanded && breakdownItems.length === 0 && (
          <tr style={{ backgroundColor: 'rgba(0,0,0,0.005)' }}>
            <td colSpan={2} style={{ paddingLeft: '72px', color: 'var(--text-muted)', fontSize: '11px', fontStyle: 'italic' }}>
              Tidak ada data transaksi Buku Besar untuk sub-akun ini.
            </td>
          </tr>
        )}

        {isExpanded && breakdownItems.map(item => (
          <tr key={item.code} style={{ backgroundColor: 'rgba(0,0,0,0.005)' }}>
            <td style={{ paddingLeft: '72px', color: 'var(--text-secondary)', fontSize: '12px' }}>
              <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', marginRight: '8px' }}>[{item.code}]</span>
              {item.name}
            </td>
            <td style={{ textAlign: 'right', fontSize: '12px', color: item.balance < 0 ? '#ef4444' : 'inherit' }}>
              <span 
                className="drillable-value"
                onClick={() => handleDrilldown(`${item.name} (${item.code})`, [item.code])}
              >
                {formatRupiah(item.balance)}
              </span>
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <div className="data-table-card animate-in" style={{ background: 'var(--bg-card)', color: 'var(--text-primary)' }}>
      {/* Styles for print clean paper layout and clickable drills */}
      <style dangerouslySetInnerHTML={{__html: `
        .paper-report-sheet {
          background: #ffffff !important;
          color: #1e293b !important;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 30px rgba(0,0,0,0.02);
          padding: 32px;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .dark .paper-report-sheet {
          background: #0f172a !important; /* Elegant dark paper adaptation */
          color: #f8fafc !important;
          border-color: #334155;
        }
        .paper-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
        }
        .paper-table th {
          border-bottom: 2px solid #1e293b;
          color: #475569;
          font-weight: 700;
          padding: 10px 14px;
        }
        .dark .paper-table th {
          border-color: #f8fafc;
          color: #94a3b8;
        }
        .paper-table td {
          padding: 10px 14px;
          border-bottom: 1px solid #f1f5f9;
        }
        .dark .paper-table td {
          border-bottom-color: #1e293b;
        }
        .paper-header-row {
          font-weight: 700;
          background: rgba(0,0,0,0.01) !important;
          border-bottom: 1px solid #1e293b !important;
          font-size: 14.5px;
        }
        .dark .paper-header-row {
          border-bottom-color: #f8fafc !important;
        }
        .paper-total-row {
          font-weight: 800;
          font-size: 14.5px;
          background: transparent !important;
        }
        .paper-total-row td {
          border-top: 1px solid #1e293b;
          border-bottom: 4px double #1e293b !important; /* Double bottom border for accounting */
          padding: 14px;
        }
        .dark .paper-total-row td {
          border-top-color: #f8fafc;
          border-bottom-color: #f8fafc !important;
        }
        .drillable-value {
          font-family: 'monospace';
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
          padding: 2px 6px;
          border-radius: 4px;
          white-space: nowrap;
        }
        .drillable-value:hover {
          color: ${accentColor} !important;
          background: ${accentGlow};
          text-decoration: underline;
        }
        .drill-modal-overlay {
          position: fixed;
          inset: 0;
          left: 260px;
          background: rgba(15, 23, 42, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.25s ease;
          transition: left var(--transition-normal);
        }
        .main-content.sidebar-collapsed .drill-modal-overlay {
          left: 80px;
        }
        @media (max-width: 768px) {
          .drill-modal-overlay {
            left: 0 !important;
          }
        }
        .drill-modal {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 28px;
          width: 900px;
          max-width: 92vw;
          max-height: 80vh;
          box-shadow: var(--shadow-2xl);
          display: flex;
          flex-direction: column;
          gap: 16px;
          animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        .mini-spinner {
          width: 10px;
          height: 10px;
          border: 2px solid var(--border-light);
          border-top-color: ${accentColor};
          border-radius: 50%;
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        .expand-btn-toggle:hover {
          background: ${accentGlow};
        }
        .paper-copy-btn {
          position: absolute;
          top: 24px;
          right: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 14px;
          font-size: 12.5px;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          z-index: 10;
        }
        .dark .paper-copy-btn {
          background: #1e293b;
          border-color: #334155;
          color: #94a3b8;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        .paper-copy-btn:hover {
          background: ${accentGlow};
          color: ${accentColor};
          border-color: ${accentColor};
          transform: translateY(-1px);
          box-shadow: 0 4px 12px ${accentGlow};
        }
        .paper-copy-btn:active {
          transform: translateY(0);
        }
        .paper-copy-btn.copied {
          background: #ecfdf5;
          color: #059669;
          border-color: #059669;
        }
        .dark .paper-copy-btn.copied {
          background: rgba(5, 150, 105, 0.15);
          color: #34d399;
          border-color: #34d399;
        }
      `}} />

      <div className="data-table-header" style={{ borderBottom: 'none', paddingBottom: '8px' }}>
        <div>
          <div className="data-table-title" style={{ fontSize: '20px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>⚖️</span> Balance Sheet
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Laporan posisi keuangan — {mode === 'consolidated' ? `Area ${area}` : selectedBranch || 'Pilih cabang'} • {MONTHS[monthIdx]?.label}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          {(['consolidated', 'branch'] as const).map(v => (
            <button
              key={v}
              onClick={() => setMode(v)}
              style={{
                padding: '6px 14px', fontSize: '12px', fontWeight: '600',
                borderRadius: '6px', border: '1px solid var(--border-light)',
                cursor: 'pointer',
                backgroundColor: mode === v ? accentColor : 'var(--bg-secondary)',
                color: mode === v ? '#fff' : 'var(--text-muted)',
                transition: 'all 0.15s ease', fontFamily: 'Inter, sans-serif',
              }}
            >
              {v === 'consolidated' ? '🏢 Konsolidasi' : 'Per Cabang'}
            </button>
          ))}
        </div>
      </div>

      {/* Filters Panel */}
      <div style={{ padding: '16px 24px', display: 'flex', gap: '16px', borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.01)' }}>
        <div style={{ flex: '0 0 auto' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Bulan Laporan</label>
          <select
            value={monthIdx}
            onChange={e => setMonthIdx(parseInt(e.target.value))}
            style={{
              padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border-color)',
              borderRadius: '6px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
              cursor: 'pointer', outline: 'none'
            }}
          >
            {MONTHS.slice(startIdx, endIdx + 1).map((m, i) => (
              <option key={startIdx + i} value={startIdx + i}>{m.label}</option>
            ))}
          </select>
        </div>

        {mode === 'branch' && (
          <div style={{ flex: '0 0 auto' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Pilih Cabang</label>
            <select
              value={selectedBranch}
              onChange={e => setSelectedBranch(e.target.value)}
              style={{
                padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border-color)',
                borderRadius: '6px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
                cursor: 'pointer', outline: 'none'
              }}
            >
              <option value="">Pilih Cabang</option>
              {branches.map(b => (
                <option key={b.code} value={b.code}>{b.name} ({b.code})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!activeBS ? (
        <div style={{ padding: '40px', color: 'var(--text-muted)', textAlign: 'center' }}>
          {mode === 'branch' ? '⚠️ Pilih cabang terlebih dahulu untuk memuat Laporan Posisi Keuangan.' : 'Data tidak tersedia'}
        </div>
      ) : (
        <>
          {/* KPI Summary Cards */}
          <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
            {[
              { label: 'Total Assets', value: activeBS.totalAssets, color: '#3b82f6' },
              { label: 'Total Liabilities', value: Math.abs(activeBS.totalLiabilities), color: '#ef4444' },
              { label: 'Equity', value: Math.abs(activeBS.equity.total), color: '#10b981' },
              { label: 'Current Ratio', value: ratios?.currentRatio ?? 0, isRatio: true, color: (ratios?.currentRatio ?? 0) > 1 ? '#10b981' : '#ef4444' },
              { label: 'Debt-to-Equity', value: ratios?.debtToEquity ?? 0, isRatio: true, color: (ratios?.debtToEquity ?? 0) < 2 ? '#10b981' : '#ef4444' },
              { label: 'Working Capital', value: ratios?.workingCapital ?? 0, color: (ratios?.workingCapital ?? 0) > 0 ? '#10b981' : '#ef4444' },
            ].map((item, i) => {
              const itemInsights = insightsEnabled ? analyzeKPI({
                title: item.label,
                value: item.value,
                prevValue: undefined, // Balance Sheet has different periods context
                kpis: getKPIs('ALL', 0, monthIdx),
                prevKpis: null
              }) : [];

              return (
                <SmartTooltip key={i} insights={itemInsights} title={item.label}>
                  <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '600' }}>{item.label}</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: item.color }}>
                      {(item as any).isRatio ? `${(item.value as number).toFixed(2)}x` : formatRupiah(item.value as number)}
                    </div>
                  </div>
                </SmartTooltip>
              );
            })}
          </div>

          {/* Donut Charts & Trend (Side-by-side) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px', padding: '0 24px 20px' }}>
            <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>📊 Komposisi Assets</div>
              <div style={{ height: '200px' }}>
                {assetsDonut && <Doughnut data={assetsDonut} options={donutOptions} />}
              </div>
            </div>
            <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>📊 Liabilities & Equity</div>
              <div style={{ height: '200px' }}>
                {liabDonut && <Doughnut data={liabDonut} options={donutOptions} />}
              </div>
            </div>
          </div>

          {/* Trend Chart (Consolidated only) */}
          {mode === 'consolidated' && trendData && (
            <div style={{ padding: '0 24px 24px' }}>
              <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>📈 Trend Posisi Keuangan Bulanan</div>
                <SmartTooltip 
                  insights={insightsEnabled ? analyzeChartTrend(trendData.datasets[0].data, trendData.labels, 'Total Assets') : []}
                  title="Trend Assets Bulanan"
                >
                  <div style={{ height: '220px' }}>
                    <Line
                      data={trendData}
                      options={{
                        responsive: true, maintainAspectRatio: false,
                        plugins: {
                          legend: { display: true, labels: { color: chartTheme.legendColor, font: { size: 10 } } },
                          tooltip: {
                            backgroundColor: chartTheme.tooltipBg, titleColor: chartTheme.tooltipTitle,
                            bodyColor: chartTheme.tooltipBody, borderColor: chartTheme.tooltipBorder, borderWidth: 1,
                            callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${formatRupiah(ctx.raw)}` },
                          },
                          datalabels: { display: false },
                        },
                        scales: {
                          x: { ticks: { color: chartTheme.tickColor, font: { size: 9 } }, grid: { color: chartTheme.gridColorX } },
                          y: {
                            ticks: { color: chartTheme.tickColor, font: { size: 9 }, callback: (v: any) => `${(v / 1e9).toFixed(1)} M` },
                            grid: { color: chartTheme.gridColorY },
                          },
                        },
                      }}
                    />
                  </div>
                </SmartTooltip>
              </div>
            </div>
          )}

          {/* TABLE - PAPER CLEAN PROFESSIONAL STYLE */}
          <div style={{ padding: '0 24px 32px' }}>
            <div className="paper-report-sheet" style={{ position: 'relative' }}>
              <button 
                className={`paper-copy-btn ${copied ? 'copied' : ''}`}
                onClick={handleCopyTable}
                title="Salin tabel laporan keuangan"
              >
                {copied ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'scale(1.1)' }}>
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Tersalin!</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    <span>Salin Tabel</span>
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '800', letterSpacing: '0.5px' }}>LAPORAN POSISI KEUANGAN</h3>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', textTransform: 'uppercase' }}>
                  {activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}
                </span>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  Konsolidasi per {MONTHS[monthIdx]?.label} (Mata Uang: IDR)
                </div>
              </div>

              <table className="paper-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>DESKRIPSI AKUN AKUNTANSI</th>
                    <th style={{ textAlign: 'right', width: '220px' }}>NOMINAL RUPIAH</th>
                  </tr>
                </thead>
                <tbody>
                  {/* ASSETS */}
                  <tr className="paper-header-row">
                    <td>AKTIVA (ASSETS)</td>
                    <td />
                  </tr>
                  
                  {/* Current Assets Subtotal */}
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Aset Lancar (Current Assets)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Gabungan Aset Lancar', ['211', '212', '213', '214', '215', '216', '217', '218', '219'])}
                      >
                        {formatRupiah(activeBS.currentAssets.total)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow(
                    'cashBank',
                    'Kas & Bank',
                    activeBS.currentAssets.cashBank,
                    ['211', '212', '213', '219'],
                    'ASSET'
                  )}
                  {renderSubAccountRow(
                    'receivables',
                    'Piutang Usaha (Receivables)',
                    activeBS.currentAssets.receivables,
                    ['214'],
                    'ASSET'
                  )}
                  {renderSubAccountRow(
                    'prepaidAdvances',
                    'Uang Muka & Prepaid',
                    activeBS.currentAssets.prepaidAdvances ?? 0,
                    ['215', '217', '218'],
                    'ASSET'
                  )}

                  {/* Fixed Assets Subtotal */}
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Aset Tetap Net (Fixed Assets Net)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Gabungan Aset Tetap', ['221', '222', '223'])}
                      >
                        {formatRupiah(activeBS.fixedAssets.net)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow(
                    'fixedGross',
                    'Aset Tetap Gross',
                    activeBS.fixedAssets.gross,
                    ['221', '222'],
                    'ASSET'
                  )}
                  {renderSubAccountRow(
                    'accumDepr',
                    'Akumulasi Depresiasi',
                    activeBS.fixedAssets.accumDepreciation,
                    ['223'],
                    'ASSET',
                    undefined,
                    true
                  )}

                  {/* Other Assets */}
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Aset Lainnya (Other Assets)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Aset Lainnya / Pajak Tangguhan', ['224', '225'])}
                      >
                        {formatRupiah(activeBS.otherAssets.deferredTax)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow(
                    'otherAssets',
                    'Aset Lainnya (Other Assets)',
                    activeBS.otherAssets.deferredTax,
                    ['224', '225'],
                    'ASSET'
                  )}

                  {/* TOTAL ASSETS (Accounting style double underline) */}
                  <tr className="paper-total-row">
                    <td style={{ paddingLeft: '24px', color: accentLight }}>TOTAL AKTIVA (TOTAL ASSETS)</td>
                    <td style={{ textAlign: 'right', fontWeight: '800' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Total Seluruh Aktiva', ['21', '22'])}
                      >
                        {formatRupiahFull(activeBS.totalAssets)}
                      </span>
                    </td>
                  </tr>

                  {/* Spacer row */}
                  <tr style={{ height: '24px', border: 'none' }}><td colSpan={2} style={{ border: 'none' }} /></tr>

                  {/* LIABILITIES & EQUITY */}
                  <tr className="paper-header-row">
                    <td>PASIVA (LIABILITIES & EQUITY)</td>
                    <td />
                  </tr>

                  {/* Current Liabilities */}
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Kewajiban Jangka Pendek (Current Liabilities)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Gabungan Kewajiban Lancar', ['41', '42', '43', '44'])}
                      >
                        {formatRupiah(activeBS.currentLiabilities.total)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow(
                    'payables',
                    'Hutang Usaha (Payables)',
                    activeBS.currentLiabilities.payables,
                    ['411', '412', '413'],
                    'LIABILITY'
                  )}
                  {renderSubAccountRow(
                    'accruedTax',
                    'Akrual Pajak & BPJS',
                    activeBS.currentLiabilities.accruedTax + (activeBS.currentLiabilities.accruedBPJS ?? 0),
                    ['421', '420'],
                    'LIABILITY'
                  )}
                  {renderSubAccountRow(
                    'accruedSalary',
                    'Akrual Gaji Karyawan',
                    activeBS.currentLiabilities.accruedSalary,
                    ['431', '430'],
                    'LIABILITY'
                  )}
                  {renderSubAccountRow(
                    'accruedExpenses',
                    'Akrual Biaya Operasional',
                    activeBS.currentLiabilities.accruedExpenses,
                    ['441', '440'],
                    'LIABILITY'
                  )}

                  {/* Long Term Liabilities */}
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Kewajiban Jangka Panjang (Long Term Liabilities)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Kewajiban Jangka Panjang', ['451', '450'])}
                      >
                        {formatRupiah(activeBS.longTermLiabilities)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow(
                    'longTermLiab',
                    'Kewajiban Jangka Panjang (Long Term Liabilities)',
                    activeBS.longTermLiabilities,
                    ['451', '450'],
                    'LIABILITY'
                  )}

                  {/* Equity */}
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Ekuitas (Equity)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Gabungan Ekuitas Pemilik', ['30', '31', '32', '33'])}
                      >
                        {formatRupiah(activeBS.equity.total)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow(
                    'openingBalance',
                    'Modal Awal (Opening Balance)',
                    activeBS.equity.openingBalance,
                    [],
                    'EQUITY',
                    (code) => code === '30000' || code.startsWith('30')
                  )}
                  {renderSubAccountRow(
                    'retainedEarnings',
                    'Retained Earnings',
                    activeBS.equity.retainedEarnings,
                    ['31', '32', '33'],
                    'EQUITY'
                  )}

                  {/* TOTAL LIABILITIES & EQUITY */}
                  <tr className="paper-total-row">
                    <td style={{ paddingLeft: '24px', color: accentLight }}>TOTAL PASIVA (TOTAL LIABILITIES & EQUITY)</td>
                    <td style={{ textAlign: 'right', fontWeight: '800' }}>
                      <span 
                        className="drillable-value"
                        onClick={() => handleDrilldown('Total Seluruh Pasiva', ['3', '4'])}
                      >
                        {formatRupiahFull(Math.abs(activeBS.totalLiabilities) + Math.abs(activeBS.equity.total))}
                      </span>
                    </td>
                  </tr>

                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* DRILLDOWN JURNAL MODAL POPUP */}
      {showModal && (
        <div className="drill-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="drill-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📖</span> Drilldown Jurnal Keuangan
                </h3>
                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Detail baris transaksi untuk: <strong>{drilldownTitle}</strong> ({MONTHS[monthIdx]?.label})
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {glLoading ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 16px', width: '36px', height: '36px', borderRadius: '50%', border: '3px solid var(--border-light)', borderTopColor: accentColor, animation: 'spin 1s linear infinite' }} />
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Mengekstrak baris jurnal transaksi Buku Besar...</div>
              </div>
            ) : drilldownRows.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Tidak ditemukan baris transaksi Buku Besar yang cocok pada filter periode/cabang ini.
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh' }}>
                <table className="data-table" style={{ width: '100%', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Bulan</th>
                      <th style={{ textAlign: 'left' }}>Nama Akun (COA)</th>
                      <th style={{ textAlign: 'left' }}>Keterangan / Deskripsi</th>
                      <th style={{ textAlign: 'left' }}>Cabang</th>
                      <th style={{ textAlign: 'right' }}>Debit</th>
                      <th style={{ textAlign: 'right' }}>Kredit</th>
                      <th style={{ textAlign: 'right' }}>Net Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownRows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.month}</td>
                        <td style={{ fontWeight: '600' }}>{row.accName}</td>
                        <td style={{ color: 'var(--text-accent)', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.desc}>
                          {row.desc}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: '700' }}>{row.branch}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{row.debit > 0 ? formatRupiah(row.debit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{row.credit > 0 ? formatRupiah(row.credit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: row.amount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)' }}>
                          {formatRupiah(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                Ditemukan <strong>{drilldownRows.length}</strong> baris transaksi jurnal.
              </span>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ padding: '6px 14px', fontSize: '12px' }}>
                Tutup Rincian
              </button>
            </div>
          </div>
        </div>
      )}
      <style dangerouslySetInnerHTML={{__html: `
        @keyframes spin { to { transform: rotate(360deg); } }
      `}} />
    </div>
  );
};

export default BalanceSheetPage;
